import { Edge } from 'reactflow';
import { AppNode } from '../types';

const DEFAULT_W = 220;
const DEFAULT_H = 100;
const VERT_BIAS = 0.8;   // |dy|/|dx| ratio required to prefer Top
const MIN_DY = 20;       // minimum downward gap to switch to Top (prevents flicker)

interface Center { x: number; y: number; }

function worldCenter(node: AppNode, byId: Map<string, AppNode>): Center {
  const w = node.width ?? DEFAULT_W;
  const h = node.height ?? DEFAULT_H;
  let x = node.position.x + w / 2;
  let y = node.position.y + h / 2;
  if (node.parentNode) {
    const parent = byId.get(node.parentNode);
    if (parent) {
      x += parent.position.x;
      y += parent.position.y;
    }
  }
  return { x, y };
}

function isStartNode(node: AppNode): boolean {
  return !!node.type?.includes('StartEvent');
}

function isBoundaryNode(node: AppNode): boolean {
  return !!node.parentNode;
}

export function resolveTargetHandles(nodes: AppNode[], edges: Edge[]): Edge[] {
  const byId = new Map<string, AppNode>();
  for (const n of nodes) byId.set(n.id, n);

  let anyChanged = false;
  const out = edges.map((edge) => {
    const src = byId.get(edge.source);
    const tgt = byId.get(edge.target);
    if (!src || !tgt) return edge;

    // Target is a start event: has no target handle — leave untouched
    if (isStartNode(tgt)) return edge;

    // Target is a boundary event: only one (Top) target handle, unnamed — leave untouched
    if (isBoundaryNode(tgt)) return edge;

    const sc = worldCenter(src, byId);
    const tc = worldCenter(tgt, byId);
    const dx = tc.x - sc.x;
    const dy = tc.y - sc.y;

    let desired: 't-left' | 't-top';
    if (dy > MIN_DY && dy > Math.abs(dx) * VERT_BIAS) {
      desired = 't-top';
    } else {
      desired = 't-left';
    }

    if (edge.targetHandle === desired) return edge;
    anyChanged = true;
    return { ...edge, targetHandle: desired };
  });

  return anyChanged ? out : edges;
}
